import { useEffect } from 'react'

import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

/* A transient confirmation with one optional action — the undo pattern
 * (undo over confirm): the act completed, the toast says so,
 * and the action un-does it while the moment lasts.
 *
 * What this is NOT for: errors. Field errors sit beside the field, record
 * errors above the record, connection loss is a persistent bar — a toast that
 * vanishes mid-outage is worse than nothing. Nothing
 * here should ever be red.
 *
 * The live region wraps the conditional content rather than being it, so the
 * region exists BEFORE the announcement — inserting an aria-live element
 * together with its text is exactly the case screen readers miss.
 */

export interface ToastProps {
  open: boolean
  message: string
  /** The one action — "Undo", typically. Label and handler together. */
  actionLabel?: string
  onAction?: () => void
  onDismiss: () => void
  /** How long the moment lasts. Long enough to read and reconsider; short
   *  enough that the screen is not wearing yesterday's confirmation. */
  durationMs?: number
}

export function Toast({
  open,
  message,
  actionLabel,
  onAction,
  onDismiss,
  durationMs = 6000,
}: ToastProps) {
  useEffect(() => {
    if (!open) return
    const timer = setTimeout(onDismiss, durationMs)
    return () => clearTimeout(timer)
  }, [open, message, durationMs, onDismiss])

  return (
    <div
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-5 z-50 flex justify-center px-4',
      )}
    >
      {open ? (
        <div
          className={cn(
            'bg-ink text-text-on-brand pointer-events-auto flex items-center gap-3',
            'rounded-[var(--radius-DEFAULT)] py-2 pr-2 pl-4 shadow-lg',
            'text-[13px]',
          )}
        >
          <span>{message}</span>
          {actionLabel && onAction ? (
            <Button variant="ghost" size="sm" onClick={onAction} className="text-text-on-brand">
              {actionLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
